import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArbieMascot } from '../components/ArbieMascot';
import { Spinner } from '../components/ui/Spinner';
import { Alert, Button, Card, CardBody } from '../components/ui';
import { useAuth } from '../hooks/useAuth';

const BASE_URL = import.meta.env.VITE_API_URL || '';

const MagicLinkVerify: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get the redirect path from location state, default to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid magic link');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await fetch(`${BASE_URL}/api/auth/magic-link/verify/${token}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Verification failed' }));
          throw new Error(errorData.error || 'Invalid or expired magic link');
        }

        // Successfully authenticated - redirect to destination
        // Give a small delay to ensure cookie is set
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 100);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Verification failed');
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token, navigate, from]);

  // If already authenticated (from cookie), redirect
  useEffect(() => {
    if (!authLoading && isAuthenticated && !isVerifying) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, isVerifying, navigate, from]);

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ArbieMascot size="lg" className="mx-auto mb-4" />
          <Spinner size="lg" className="mx-auto mb-2" />
          <p className="text-gray-600">Verifying your login link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex flex-col items-center">
            <ArbieMascot size="lg" className="mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Recruiter Buddy</h1>
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <Card variant="elevated">
            <CardBody className="py-8 px-6 sm:px-10">
              <div className="space-y-4">
                <Alert variant="error">{error}</Alert>

                <p className="text-sm text-gray-600">
                  This magic link may have expired or already been used. Links in notification
                  emails are valid for 7 days; login page links expire after 15 minutes. Each
                  link can only be used once.
                </p>

                <Button
                  type="button"
                  onClick={() => navigate('/login')}
                  fullWidth
                  size="lg"
                >
                  Back to Login
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Should not reach here, but just in case
  return null;
};

export default MagicLinkVerify;
